import { lazy, Suspense } from 'react'

import { createFileRoute, retainSearchParams } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { actionQueries } from '@/features/actions/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import {
	Section,
	SectionDescription,
	SectionTitle,
} from '@/features/app-shell/section'
import { PlaybooksSkeleton } from '@/features/playbooks/components/skeletons'
import { playbookQueries } from '@/features/playbooks/queries'
import { playbookSearchSchema } from '@/features/playbooks/utils/search'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'
import { TableFullSkeleton } from '@/shared/data-table/components/table-skeleton'

import { ErrorBoundary } from '@/components/common/error-boundary'

const PlaybooksPage = lazy(() => import('@/features/playbooks/components/page'))

export const Route = createFileRoute('/_dashboard/playbooks/')({
	validateSearch: zodValidator(playbookSearchSchema),

	search: {
		middlewares: [retainSearchParams(['q'])],
	},

	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	loader: ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		// Prefetch both halves of the table's data — the playbook list carries
		// action ids only, so the rows need the actions catalog to name them
		if (tenant) {
			queryClient.ensureQueryData(playbookQueries.list(tenant))
			queryClient.ensureQueryData(actionQueries.list(tenant))
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Playbooks | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: PlaybooksSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	return (
		<Section>
			<SectionTitle>Playbooks</SectionTitle>
			<SectionDescription>
				Predefined workflows made up of multiple automated actions that work
				together to remediate a security incident. Playbooks can be run manually
				or automatically triggered by mapped policies.
			</SectionDescription>

			<ErrorBoundary feature="playbooks">
				<Suspense
					fallback={<TableFullSkeleton rows={5} withLastUpdated withFilters />}
				>
					<PlaybooksPage />
				</Suspense>
			</ErrorBoundary>
		</Section>
	)
}
